import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, unwrapApiResponse } from '@/utils/api';
import { LevelBadge } from '@/components/ranking/LevelBadge';
import { getUserImageUrl, getMolduraImageUrl, avatarClipClassFromMolduraTipo } from '@/utils/image-utils';
import Navbar from '@/components/layout/Navbar';
import MobileBottomNav from '@/components/layout/MobileBottomNav';

export default function RankingPage() {
    const [ranking, setRanking] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [rankingType, setRankingType] = useState<'total' | 'semanal'>('semanal');
    const [nextReset, setNextReset] = useState<string>('');

    useEffect(() => {
        const fetchRanking = async () => {
            setLoading(true);
            try {
                if (rankingType === 'total') {
                    const data = unwrapApiResponse<any>(await api.get('xp/ranking?limit=100', { requiresAuth: false }));
                    const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
                    setRanking(items);
                } else {
                    const data = unwrapApiResponse<any>(await api.get('xp/ranking/semanal?limit=100', { requiresAuth: false }));
                    const items = Array.isArray(data?.items) ? data.items : [];
                    setRanking(items);
                }
            } catch (error) {
                console.error('Erro ao carregar ranking:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRanking();
    }, [rankingType]);

    /** Alinha ao backend (`inicioDaSemanaSP`): semana começa segunda 00:00 America/Sao_Paulo. */
    useEffect(() => {
        const inicioDoDiaSP = (d: Date) => {
            const offsetMs = 3 * 60 * 60 * 1000;
            const local = new Date(d.getTime() - offsetMs);
            local.setUTCHours(0, 0, 0, 0);
            return new Date(local.getTime() + offsetMs);
        };
        const inicioDaSemanaSP = (d = new Date()) => {
            const dia = inicioDoDiaSP(d);
            const wd = dia.getUTCDay();
            const diasAtras = (wd + 6) % 7;
            return new Date(dia.getTime() - diasAtras * 24 * 60 * 60 * 1000);
        };
        const calculateNextReset = () => {
            const now = new Date();
            const proximaSegunda = new Date(inicioDaSemanaSP(now).getTime() + 7 * 24 * 60 * 60 * 1000);
            const diff = proximaSegunda.getTime() - now.getTime();
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            if (days > 0) {
                setNextReset(`${days}d ${hours}h`);
            } else {
                setNextReset(`${Math.max(0, hours)}h`);
            }
        };
        calculateNextReset();
        const interval = setInterval(calculateNextReset, 1000 * 60 * 60);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
            </div>
        );
    }

    const topThree = ranking.slice(0, 3);
    const rest = ranking.slice(3);

    return (
        <div className="min-h-screen bg-black text-white pb-24 md:pb-10">
            <Navbar />
            <main className="max-w-4xl mx-auto px-4 pt-4 lg:pt-36 pb-32">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 px-2">
                    <div className="flex items-center gap-4">
                        <div className="w-1.5 h-10 bg-orange-600 rounded-full" />
                        <div>
                            <h1 className="text-2xl md:text-4xl font-black tracking-tighter uppercase italic">Ranking Global</h1>
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 mt-1">
                                {rankingType === 'semanal' && nextReset ? `Próxima atualização em: ${nextReset}` : 'Top leitores da plataforma'}
                            </p>
                        </div>
                    </div>
                    <div className="flex bg-neutral-900/80 border border-white/10 rounded-xl p-1 gap-1 self-start md:self-auto">
                        {(['semanal', 'total'] as const).map((type) => (
                            <button
                                key={type}
                                onClick={() => setRankingType(type)}
                                className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${rankingType === type ? 'bg-orange-500 text-white shadow-lg' : 'text-white/40 hover:text-white/70'}`}
                            >
                                {type === 'semanal' ? 'Semanal' : 'Total'}
                            </button>
                        ))}
                    </div>
                </div>

                {topThree.length > 0 && (
                    <div className="flex flex-col md:flex-row justify-center items-end gap-3 md:gap-4 mb-12">
                        {/* Podium items (2nd, 1st, 3rd) simplified for Vite */}
                        {topThree[1] && (
                            <div className="order-2 md:order-1 flex flex-col items-center w-full md:w-[28%] bg-neutral-900/50 border border-white/10 rounded-xl p-6 relative overflow-hidden">
                                <div className="absolute top-3 right-3 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center font-bold text-white text-sm">2</div>
                                <Link to={`/perfil/${topThree[1].usr_nick}`} className="flex flex-col items-center">
                                    <div className={`w-20 h-20 md:w-24 md:h-24 ${avatarClipClassFromMolduraTipo(topThree[1].moldura_ativa?.mol_tipo)} mb-4 relative`}>
                                        {topThree[1].moldura_ativa?.mol_imagem && <img src={getMolduraImageUrl(topThree[1].moldura_ativa.mol_imagem) || ''} alt="Moldura" className="absolute -top-[35%] w-[170%] h-[170%] z-20 object-contain" />}
                                        <img src={getUserImageUrl(topThree[1].usr_id, topThree[1].usr_imagem)} alt={topThree[1].usr_nick} className={`w-full h-full object-cover ${avatarClipClassFromMolduraTipo(topThree[1].moldura_ativa?.mol_tipo)} border-2 border-white/30`} />
                                        <div className="absolute -bottom-1 -right-1 z-30"><LevelBadge level={topThree[1].usr_nivel || 1} size="sm" /></div>
                                    </div>
                                    <h3 className="text-white font-bold text-sm truncate w-full text-center hover:text-orange-400">{topThree[1].usr_nick}</h3>
                                </Link>
                                <p className="text-white/60 text-xs font-medium mt-1">{new Intl.NumberFormat('pt-BR').format(topThree[1].usr_xp)} XP</p>
                            </div>
                        )}
                        <div className="order-1 md:order-2 flex flex-col items-center w-full md:w-[36%] z-10 bg-gradient-to-b from-orange-500/20 to-neutral-900/50 border border-orange-500/40 rounded-xl p-8 relative overflow-hidden shadow-orange-600/10">
                            <div className="absolute top-4 right-4 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center font-bold text-white shadow-lg">1</div>
                            <Link to={`/perfil/${topThree[0].usr_nick}`} className="flex flex-col items-center">
                                <div className={`w-28 h-28 md:w-32 md:h-32 ${avatarClipClassFromMolduraTipo(topThree[0].moldura_ativa?.mol_tipo)} mb-5 relative`}>
                                    {topThree[0].moldura_ativa?.mol_imagem && <img src={getMolduraImageUrl(topThree[0].moldura_ativa.mol_imagem) || ''} alt="Moldura" className="absolute -top-[35%] w-[170%] h-[170%] z-20 object-contain" />}
                                    <img src={getUserImageUrl(topThree[0].usr_id, topThree[0].usr_imagem)} alt={topThree[0].usr_nick} className={`w-full h-full object-cover ${avatarClipClassFromMolduraTipo(topThree[0].moldura_ativa?.mol_tipo)} border-[3px] border-orange-500/60 shadow-lg`} />
                                    <div className="absolute -bottom-1 -right-1 z-30"><LevelBadge level={topThree[0].usr_nivel || 1} size="md" /></div>
                                </div>
                                <h3 className="text-white font-bold text-base md:text-lg truncate w-full text-center hover:text-orange-400">{topThree[0].usr_nick}</h3>
                            </Link>
                            <p className="text-orange-400 font-bold text-sm md:text-base mt-2">{new Intl.NumberFormat('pt-BR').format(topThree[0].usr_xp)} XP</p>
                        </div>
                        {topThree[2] && (
                            <div className="order-3 md:order-3 flex flex-col items-center w-full md:w-[28%] bg-neutral-900/50 border border-white/10 rounded-xl p-6 relative overflow-hidden">
                                <div className="absolute top-3 right-3 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center font-bold text-white text-sm">3</div>
                                <Link to={`/perfil/${topThree[2].usr_nick}`} className="flex flex-col items-center">
                                    <div className={`w-20 h-20 md:w-24 md:h-24 ${avatarClipClassFromMolduraTipo(topThree[2].moldura_ativa?.mol_tipo)} mb-4 relative`}>
                                        {topThree[2].moldura_ativa?.mol_imagem && <img src={getMolduraImageUrl(topThree[2].moldura_ativa.mol_imagem) || ''} alt="Moldura" className="absolute -top-[35%] w-[170%] h-[170%] z-20 object-contain" />}
                                        <img src={getUserImageUrl(topThree[2].usr_id, topThree[2].usr_imagem)} alt={topThree[2].usr_nick} className={`w-full h-full object-cover ${avatarClipClassFromMolduraTipo(topThree[2].moldura_ativa?.mol_tipo)} border-2 border-white/30`} />
                                        <div className="absolute -bottom-1 -right-1 z-30"><LevelBadge level={topThree[2].usr_nivel || 1} size="sm" /></div>
                                    </div>
                                    <h3 className="text-white font-bold text-sm truncate w-full text-center hover:text-orange-400">{topThree[2].usr_nick}</h3>
                                </Link>
                                <p className="text-white/60 text-xs font-medium mt-1">{new Intl.NumberFormat('pt-BR').format(topThree[2].usr_xp)} XP</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    {rest.map((user, index) => (
                        <Link to={`/perfil/${user.usr_nick}`} key={user.usr_id} className="block w-full">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-900/40 border border-white/10 hover:bg-neutral-900/60 transition-colors">
                                <div className="w-8 flex-shrink-0"><span className="text-white/40 text-sm font-medium">#{index + 4}</span></div>
                                <div className="relative w-10 h-10">
                                    {user.moldura_ativa?.mol_imagem && <img src={getMolduraImageUrl(user.moldura_ativa.mol_imagem) || ''} alt="Moldura" className="absolute -top-[35%] w-[180%] h-[180%] z-20 object-contain" />}
                                    <div className={`w-10 h-10 ${avatarClipClassFromMolduraTipo(user.moldura_ativa?.mol_tipo)} overflow-hidden border border-white/10 relative z-10 bg-neutral-800`}>
                                        <img src={getUserImageUrl(user.usr_id, user.usr_imagem)} alt={user.usr_nick} className="w-full h-full object-cover" />
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-medium text-white text-sm truncate">{user.usr_nick || 'Usuário'}</h3>
                                        <LevelBadge level={user.usr_nivel || 1} size="sm" />
                                    </div>
                                </div>
                                <div className="text-right"><span className="text-orange-400 text-sm font-medium">{new Intl.NumberFormat('pt-BR').format(user.usr_xp)} XP</span></div>
                            </div>
                        </Link>
                    ))}
                    {ranking.length === 0 && <div className="text-center py-24 bg-neutral-900/40 border border-white/10 rounded-xl"><p className="text-white/30 text-sm">Nenhum usuário encontrado no ranking.</p></div>}
                </div>
            </main>
            <MobileBottomNav />
        </div>
    );
}
